import Decimal from 'decimal.js-light';
import React from 'react';
import { useSelector } from 'react-redux';

import { getL10nNumber } from './L10nNumber';

import { getCurrency, getIsGlobalCurrency, getMarket } from '../selectors';
import { getCurrencyDecimals, getCurrencyPatternWithFlag, getCurrencySign } from '../utils/l10n';

export type Props = {
  market?: string;
  currency?: string;
  value: string | number | null;
  locale?: string;
  isGlobalCurrency?: boolean;
  roundPosition?: number | null;
  shouldDisplayOriginalValue?: boolean;
  isJPYAlternative?: boolean;
  className?: string;
};

export const DEFAULT_CURRENCY = 'USD';

export function turnIntoRoundedValue(value: number, roundPosition: number): number {
  if (roundPosition > 0) {
    const exponentialModifier = Math.pow(10, roundPosition);
    const roundedValue = Math.trunc(value * exponentialModifier) / exponentialModifier;

    return roundedValue < 1 ? value : roundedValue;
  } else {
    return value;
  }
}

export function getL10nMoney(
  market: string,
  value: string | number,
  currency = DEFAULT_CURRENCY,
  isGlobalCurrency = false,
  roundPosition?: number | null,
  shouldDisplayOriginalValue = false,
  isJPYAlternative = true,
) {
  // to format display of value based on currency's default round position
  let numberDecimal = new Decimal(value);
  let numberAbsDecimal = numberDecimal.abs();
  let numberValue = getL10nNumber(
    market,
    numberAbsDecimal,
    getCurrencyDecimals(currency),
    shouldDisplayOriginalValue,
  );

  // to format display of value if roundPosition prop was provided
  if (typeof roundPosition === 'number' && !shouldDisplayOriginalValue) {
    if (roundPosition < 0) {
      const roundedDecimalNumber = new Decimal(turnIntoRoundedValue(+value, roundPosition));
      numberValue = getL10nNumber(market, roundedDecimalNumber.abs());
    } else {
      numberValue = getL10nNumber(market, numberAbsDecimal, roundPosition);
    }
  }

  const pattern = getCurrencyPatternWithFlag(currency, isJPYAlternative, isGlobalCurrency);
  const sign = getCurrencySign(currency, isGlobalCurrency);

  if (numberDecimal.gte(0)) {
    return pattern.positive.replace('{amount}', numberValue).replace('{sign}', sign);
  }

  return pattern.negative.replace('{amount}', numberValue).replace('{sign}', sign);
}

function L10nMoney({
  market,
  value,
  currency,
  isGlobalCurrency,
  roundPosition,
  shouldDisplayOriginalValue = false,
  isJPYAlternative = true,
  className,
}: Props) {
  const marketFromStore = useSelector(getMarket)?.marketCode || '';
  const currencyFromStore = useSelector(getCurrency) || '';
  const shouldUseGlobalCurrency = useSelector(getIsGlobalCurrency) || isGlobalCurrency;

  return !value && value !== 0 ? null : (
    <span className={className}>
      {getL10nMoney(
        market || marketFromStore,
        value,
        currency || currencyFromStore,
        shouldUseGlobalCurrency,
        roundPosition,
        shouldDisplayOriginalValue,
        isJPYAlternative,
      )}
    </span>
  );
}

L10nMoney.defaultProps = {
  value: null,
};

export default L10nMoney;
